import { Icon, Loading, Popup, Tooltip } from '@startlibs/components';
import {ToggleCheckbox} from '@startlibs/form'
import { isMobile, callIfFunction, getColor, media } from '@startlibs/utils';
import {usePopupToggle} from '@startlibs/core'
import React, {useMemo, useRef, useState} from 'react'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components'

import {StudyRow} from './StudyRow'
import {TOOLS_KEYS, TOOLS_LABELS, TOOLS_ICONS} from '../../enums/Tools'
import { ToolButton } from './ToolsCell';
import {useWhenApplyShadow} from '../../hooks/useWhenApplyShadow'

const WorklistTable = styled.div`
  width: 100%;
  color: ${getColor('gray60')};
  background: white;
  border: 1px solid rgba(0,0,0,0.15);
  border-spacing: 0;
  position: relative;
  ${props => props.isLoading && `
    pointer-events: none;
    ${WorklistTableRow} {
      border-color: transparent;
      > * {
        opacity: 0.1;
        transition: 0.5s ease;
      }
    }
  `}
  ${media.desktop `
    border-radius: 5px;
  `}
  ${props => props.isMobile && css `
      overflow-x: auto;
  `}
`

export const WorklistTableRow = styled.div`
  min-height: 4rem;
  position: relative;
  border-bottom: 1px solid rgb(225,225,225);
  display: flex;
  background: white;
  :nth-child(even) {
    background: #f0f6f8;
    .tools {
      background: #f0f6f8;
    }
    ${ToolButton} {
      :after {
        border-color: #f0f6f8;
      }
      :before {
        background-color: #f0f6f8;
      }
    }
  }
  :last-child {
    border: none;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
    .tools {
      border-bottom-right-radius: 5px;
    }
  }
  ${props => props.isMobile && css `
    min-width: 100rem;
  `}
`
export const WorklistTableHead = styled(WorklistTableRow)`
  align-items: center;
  font-weight: 600;
  background: white;
  top: 0;
  position: sticky;
  z-index: 15;
  border-top-left-radius:5px;
  border-top-right-radius:5px;
  border-color: rgba(0,0,0,0.15) !important;
  > * {
    opacity: 1 !important;
  }
  .tools {
    border-top-right-radius: 5px;
    ${props => props.isMobile && `
      height: 4rem;
    `}
  }
`
export const WorklistTableColumn = styled.div`
  flex-basis: 0;
  flex-grow: 2;
  padding: 0.75rem 0.75rem;
  :first-child {
    padding-left: 1rem;
  }
  :last-child {
    padding-right: 1rem;
  }
  &.patient {
    flex-grow: 3;
  }
  &.study {
    flex-grow: 3;
  }
  &.studyDate {
    flex-grow: 0;
    width: 120px;
    flex-basis: inherit;
  }
  &.uploadDate {
    flex-grow: 0;
    width: 90px;
    flex-basis: inherit;
  }
  &.status {
    flex-grow: 0;
    width: 90px;
    flex-basis: inherit;
  }
  &.tools {
    background: white;
    flex-grow: 0;
    width: ${props => props.isMobile ? "3rem" : (props.activeToolsAmount * 30 + 20 + "px")};
    margin-left: 20px;
    min-width: 46px;
    flex-basis: inherit;
    text-align: right;
    position: sticky;
    right: 0rem;
    z-index: 10;
    :before {
      transition: all 0.5s ease, opacity 0ms;
      content: '';
      position: absolute;
      width: 0;
      left: 0;
      bottom: 0;
      top: 0;
      pointer-events: none;
      background: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.05) 100%);
      border-right: 1px solid rgba(0,0,0,0.1);
      opacity: 0;
    }
    ${props => props.hasHorizontalScroll && `
      :before {
        opacity: 1;
        width: 0.75rem;
        left: -0.75rem;
      }
    `}
  }
`
const SettingsButton = styled(Icon)`
  color: ${getColor('secondary')};
  cursor: pointer;
  border-radius: 50%;
  width: 2.5rem;
  text-align: center;
  line-height: 2.5rem;
  display: inline-block;
  font-size:24px;
  position:relative;
  margin: 0 -4px;
  :hover {
    background: rgba(41, 122, 163, 0.125);
  }
  :active {
    background: rgba(41, 122, 163, 0.175);
  }
`

const QuickActionsConfigPopup = styled(Popup)`
  padding: 0 0 0.25rem;
  .popup-header{
    padding: 1.5rem 1.5rem 0.5rem;
    h4 {
      margin-bottom: 0;
    }
    div {
      font-size: 11px;
      color: rgba(0,0,0,0.5);
    }
  }
  ${ToggleCheckbox} {
    ${Icon} {
      font-size: 25px;
      width: 2rem;
      text-align: center;
      margin-right: 0.75rem;
      display: inline-block;
      vertical-align: -6px;
    }
    background: white;
    font-size: 13px;
    font-weight: 400;
    padding: 8px 1.5rem;
    border-radius: 0;
    margin-bottom: 0.5rem;
    :hover {
      background: rgba(41, 122, 163, 0.125);
    }
    :active {
      background: rgba(41, 122, 163, 0.175);
    }
  }
`
const LoadingTableContent = styled.div `
  position: absolute;
  top: 4rem;
  left: 0;
  right: 0;
  height: 100%;
  height: calc(100% - 4rem);
  max-height: 80vh;
  max-height: calc(80vh - 4rem);
  ${props => !props.isMobile && css`
    ${media.desktop `
      max-width: calc(100vw - 5rem - 15px);
      left: 2.5rem;
    `}
    ${media.mobile `
      max-width: calc(100vw - 15px);
      left: 0;
    `}
  `}
`
const EmptyStudyList = styled.div `
  background: rgb(240,240,240);
  min-height: 8rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 14px;
  max-width: calc(100vw);
  position: sticky;
  left: 0;
`

export const Table = ({userRights, setParams, refreshList, setUserRights, rows, isLoading, blankingWorklist}) => {

  const [studies, setStudies] = useState(rows.map((row, i) => _.update('tools.attachNonDicom.content',Number,{...row, id: i})))
  const userRightsPopup = usePopupToggle()
  const tableRef = useRef()



  const activeToolsAmount = useMemo(() => {
    const pairs = _.toPairs(userRights.tools.functions)
    const active = pairs.filter(([, isVisible]) => isVisible === 1).length
    const inactive = pairs.filter(([, isVisible]) => isVisible === 0).length
    return active + (inactive > 0 ? 1 : 0)
  }, [userRights.tools.functions])
  const isApplyShadow = useWhenApplyShadow(tableRef)
  return <WorklistTable ref={tableRef} isLoading={isLoading} isMobile={isMobile()}>
    <WorklistTableHead isMobile={isMobile}>
      {userRights.patient.visible && <WorklistTableColumn className="patient">Patient</WorklistTableColumn>}
      {userRights.study.visible && <WorklistTableColumn className="study">Study</WorklistTableColumn>}
      {userRights.study.visible && <WorklistTableColumn className="studyDate">Study date</WorklistTableColumn>}
      {userRights.physician.visible && <WorklistTableColumn>Ref. Physician</WorklistTableColumn>}
      {userRights.institution.visible && <WorklistTableColumn>Institution</WorklistTableColumn>}
      {userRights.performingPhysician?.visible && <WorklistTableColumn>Perf. Physician</WorklistTableColumn>}
      {userRights.assignment.visible && <WorklistTableColumn>Assignment</WorklistTableColumn>}
      {userRights.status.visible && <WorklistTableColumn className="status">Status</WorklistTableColumn>}
      {userRights.uploadDate.visible && <WorklistTableColumn className="uploadDate">Upload date</WorklistTableColumn>}
      {
        userRights.tools.visible &&
        <WorklistTableColumn className="tools JS-fix-dialog-scroll" activeToolsAmount={activeToolsAmount} hasHorizontalScroll={isApplyShadow} isMobile={isMobile()}>
          {!isMobile() && <Tooltip content="Quick Actions options"><SettingsButton icon="settings-line" onClick={userRightsPopup.open}>
            {
              userRightsPopup.isOpen &&
              <QuickActionsConfigPopup onClick={(e) => e.stopPropagation()}>
                <div className="popup-header">
                  <h4>Quick actions</h4>
                  <div>Shortcuts to display directly in table rows</div>
                </div>
                {
                  TOOLS_KEYS.map((toolKey, i) =>
                    _.get('tools.functions.' + toolKey, userRights) >= 0 &&
                    <ToggleCheckbox
                      raw
                      key={toolKey}
                      value={_.get('tools.functions.' + toolKey, userRights)}
                      setValue={(v) => setUserRights(_.set(toolKey, v ? 1 : 0))}
                      label={<>
                         <Icon icon={TOOLS_ICONS[toolKey]}/>{TOOLS_LABELS[toolKey]}
                        </>}
                    />
                  )
                }
              </QuickActionsConfigPopup>
            }
          </SettingsButton></Tooltip>}
        </WorklistTableColumn>
      }
    </WorklistTableHead>
    {
      studies.length
        ? studies.map(study => <StudyRow setParams={setParams} refreshList={refreshList}  isLoading={isLoading} activeToolsAmount={activeToolsAmount} key={study.id}
                                    userRights={userRights} study={study} hasHorizontalScroll={isApplyShadow} isMobile={isMobile()}
                                    setStudy={(update) => setStudies(_.map(s => s.study.id === study.study.id ? callIfFunction(update, s) : s))}/>)
      : blankingWorklist
        ? <EmptyStudyList css="min-height: 15rem;">{!isLoading && <div><h4>Search to view records</h4><div>Insert a search term above to display matching records</div></div>}</EmptyStudyList>
        : <EmptyStudyList>{!isLoading && 'No records found'}</EmptyStudyList>
    }
    {isLoading && <LoadingTableContent isMobile={isMobile()}><Loading absolute size={40} borderWidth={6}/></LoadingTableContent>}
  </WorklistTable>
}
